import { ClipboardList } from "lucide-react";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import type { ProgressSectionId } from "../lib/progress-calculator";

interface HealthQuestionsIntroScreenProps {
  onContinue: () => void;
  onBack?: () => void;
  onClose?: () => void;
  language?: "en" | "es";
  logoUrl?: string;
  logoUrlAlt?: string;
  currentStep?: number;
  totalSteps?: number;
  sectionIndex?: number;
  totalSections?: number;
  sectionId?: ProgressSectionId;
}

export function HealthQuestionsIntroScreen({
  onContinue,
  onBack,
  onClose,
  language = "en",
  logoUrl,
  logoUrlAlt,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
}: HealthQuestionsIntroScreenProps) {
  const t = getNamespaceTranslations(language, "healthQuestionsIntro");

  return (
    <div className="w-full min-h-screen flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
      <Header logoUrl={logoUrl} logoAlt={logoUrlAlt} onBack={onBack} onClose={onClose} />

      <main className="flex-1 w-full py-[30px] px-5 lg:px-4 overflow-y-auto">
        <div className="w-full max-w-[640px] mx-auto">
          {currentStep !== undefined &&
            totalSteps !== undefined &&
            sectionIndex !== undefined &&
            totalSections !== undefined &&
            sectionId !== undefined && (
            <ProgressBar
              currentStep={currentStep}
              totalSteps={totalSteps}
              sectionIndex={sectionIndex}
              totalSections={totalSections}
              sectionId={sectionId}
              language={language}
            />
          )}

          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 mt-8">
            <div className="flex gap-4 flex-row items-center">
              <ClipboardList className="w-6 h-6 text-[#0B2545] mb-4" aria-hidden="true" />
              <h1
                className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal text-center mb-4"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {t?.title}
              </h1>
            </div>

            <p
              className="text-base leading-[22px] text-gray-700 text-center mb-4"
              style={{ fontFamily: "TT Commons Pro" }}
              dangerouslySetInnerHTML={{ __html: t?.description1 || "" }}
            />

            <p
              className="text-base leading-[22px] text-gray-700 text-center mb-6"
              style={{ fontFamily: "TT Commons Pro" }}
            >
              {t?.description2}
            </p>

            <button
              type="button"
              onClick={onContinue}
              className="w-full h-[50px] inline-flex items-center justify-center rounded-full bg-[#2D55FB] text-white px-[30px] text-lg leading-6 font-medium transition-colors hover:bg-[#2347E8]"
              style={{ fontFamily: "TT Commons Pro" }}
              data-testid="health_questions_intro-continue_button"
            >
              {t?.continue}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
